// ── Industry categories with feature flags (synced from mobile — Session 12) ──
// 17 categories. Feature flags drive the industry lens on Jobs/Bookings/Dispatch pages.
// Sub-industries are NOT included (mobile-only for onboarding).

export interface CategoryFeatures {
  showBookings: boolean;
  showJobs: boolean;
  showFleet: boolean;
  showDispatch: boolean;
  showProjects: boolean;
  showOperators: boolean;
  showLocations: boolean;
}

export interface IndustryCategory {
  id: string;
  label: string;
  icon: string; // Lucide icon name
  features: CategoryFeatures;
}

export const INDUSTRY_CATEGORIES: IndustryCategory[] = [
  // Cluster A — Appointment-based
  { id: 'beauty', label: 'Beauty & Personal Care', icon: 'Scissors', features: { showBookings: true, showJobs: false, showFleet: false, showDispatch: false, showProjects: false, showOperators: true, showLocations: false } },
  { id: 'wellness', label: 'Wellness & Fitness', icon: 'Heart', features: { showBookings: true, showJobs: false, showFleet: false, showDispatch: false, showProjects: false, showOperators: false, showLocations: false } },
  { id: 'health', label: 'Health & Medical', icon: 'Stethoscope', features: { showBookings: true, showJobs: false, showFleet: false, showDispatch: false, showProjects: false, showOperators: false, showLocations: false } },
  { id: 'education', label: 'Education & Lessons', icon: 'GraduationCap', features: { showBookings: true, showJobs: false, showFleet: false, showDispatch: false, showProjects: false, showOperators: false, showLocations: false } },

  // Cluster B — Field Service
  { id: 'construction', label: 'Construction & Trades', icon: 'HardHat', features: { showBookings: false, showJobs: true, showFleet: true, showDispatch: true, showProjects: true, showOperators: false, showLocations: true } },
  { id: 'home_services', label: 'Home Services & Repairs', icon: 'Wrench', features: { showBookings: false, showJobs: true, showFleet: true, showDispatch: true, showProjects: true, showOperators: false, showLocations: true } },
  { id: 'landscaping', label: 'Landscaping & Outdoor', icon: 'TreePine', features: { showBookings: false, showJobs: true, showFleet: true, showDispatch: true, showProjects: true, showOperators: false, showLocations: true } },

  // Cluster C — Route-Based
  { id: 'cleaning', label: 'Cleaning Services', icon: 'SprayCan', features: { showBookings: false, showJobs: true, showFleet: true, showDispatch: true, showProjects: false, showOperators: false, showLocations: true } },
  { id: 'pet', label: 'Pet Services', icon: 'Dog', features: { showBookings: true, showJobs: true, showFleet: true, showDispatch: true, showProjects: false, showOperators: false, showLocations: true } },
  { id: 'care', label: 'Care & Errands', icon: 'HeartHandshake', features: { showBookings: false, showJobs: true, showFleet: false, showDispatch: false, showProjects: false, showOperators: false, showLocations: true } },

  // Cluster D — Property
  { id: 'property', label: 'Property & Facilities', icon: 'Building2', features: { showBookings: false, showJobs: true, showFleet: true, showDispatch: false, showProjects: true, showOperators: false, showLocations: true } },

  // Cluster E — Event/Project
  { id: 'events', label: 'Events & Hospitality', icon: 'PartyPopper', features: { showBookings: true, showJobs: false, showFleet: false, showDispatch: false, showProjects: true, showOperators: false, showLocations: true } },
  { id: 'creative', label: 'Professional & Creative', icon: 'Palette', features: { showBookings: false, showJobs: false, showFleet: false, showDispatch: false, showProjects: true, showOperators: false, showLocations: false } },
  { id: 'technology', label: 'Technology & IT', icon: 'Monitor', features: { showBookings: false, showJobs: true, showFleet: false, showDispatch: false, showProjects: true, showOperators: false, showLocations: false } },

  // Cluster F — Automotive
  { id: 'automotive', label: 'Automotive Services', icon: 'Car', features: { showBookings: true, showJobs: true, showFleet: true, showDispatch: false, showProjects: false, showOperators: false, showLocations: false } },

  // Cluster G — Transport
  { id: 'transportation', label: 'Transportation & Delivery', icon: 'Truck', features: { showBookings: false, showJobs: true, showFleet: true, showDispatch: true, showProjects: false, showOperators: false, showLocations: false } },

  // Catch-all
  { id: 'other', label: 'Other', icon: 'MoreHorizontal', features: { showBookings: true, showJobs: true, showFleet: true, showDispatch: true, showProjects: true, showOperators: false, showLocations: true } },
];

export function getIndustryLabel(id: string): string {
  return INDUSTRY_CATEGORIES.find(c => c.id === id)?.label ?? id;
}

export function getCategoryFeatures(categoryId: string | null | undefined): CategoryFeatures {
  const cat = INDUSTRY_CATEGORIES.find(c => c.id === categoryId);
  // Default: show everything (safe fallback for unknown industries)
  return cat?.features ?? { showBookings: true, showJobs: true, showFleet: true, showDispatch: true, showProjects: true, showOperators: false, showLocations: true };
}

// ── Industry qualifier for branded name ──

const INDUSTRY_QUALIFIERS: Record<string, string> = {
  beauty: 'Salon', pet: 'Pet', home_services: 'Trades',
  cleaning: 'Clean', landscaping: 'Landscape', automotive: 'Auto',
  wellness: 'Fitness', construction: 'Trades', property: 'Property',
  transportation: 'Fleet', events: 'Events', technology: 'Tech',
  education: 'Academy', health: 'Clinic', creative: 'Studio',
  care: 'Care', other: '',
};

export function getIndustryQualifier(categoryId: string | null | undefined): string {
  if (!categoryId) return '';
  return INDUSTRY_QUALIFIERS[categoryId] ?? '';
}

export function getBrandedName(categoryId: string | null | undefined): string {
  const q = getIndustryQualifier(categoryId);
  return q ? `CrewBit ${q}` : 'CrewBit';
}
